import { getExportStatus } from 'app/api/export';

// Triggers a download for a remote URL without opening a new tab. Avoids
// popup-blocker rejection that happens when the download fires from a setInterval
// callback (Chrome treats it as non-user-initiated). Using location.assign on
// an S3 file URL kicks off the browser's download flow for content-typed files
// without navigating away from the SPA.
export function triggerDownload(url) {
  if (!url) return;
  console.log('[exportPoller] triggering download:', url);

  // Anchor + click is the most universally honored programmatic download method.
  // Without target='_blank', Chrome doesn't classify this as a popup, and the
  // browser handles the response as a download.
  const link = document.createElement('a');
  link.href = url;
  // Hint the browser to download rather than navigate. Cross-origin S3 ignores
  // the filename here but browsers still honor the download intent.
  link.download = '';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Polls the export status endpoint until the job resolves. Uses chained
// setTimeout rather than setInterval so only one request is ever in flight —
// important while the backend export is heavy and the event loop is congested.
// Without this, polls pile up and onDone can fire multiple times once the
// server catches up.
//
// `getStatus` is optional and defaults to the shared /admin/export/status
// endpoint. Exports whose permission module isn't Analytics_and_Reporting
// (e.g. bill validation) pass their own module-scoped status fetcher here,
// because the shared route 401s admins who lack the reporting permission.
export function startExportPoll(
  jobId,
  { onDone, onFailed, onError, getStatus = getExportStatus }
) {
  let stopped = false;
  let timeoutId = null;

  async function poll() {
    if (stopped) return;
    try {
      const res = await getStatus(jobId);
      if (stopped) return;
      const { status, url, error } = res?.data || {};
      console.log('[exportPoller] poll', { jobId, status, hasUrl: !!url });

      if (status === 'done' && url) {
        stopped = true;
        onDone(url);
        return;
      }
      if (status === 'failed') {
        stopped = true;
        onFailed(error);
        return;
      }
      // 'pending' or 'processing' → schedule next poll
      timeoutId = setTimeout(poll, 4000);
    } catch (err) {
      if (stopped) return;
      console.error('[exportPoller] poll error', err);
      stopped = true;
      onError();
    }
  }

  // Initial delay matches the previous setInterval behavior — first poll fires
  // 4s after the job is created, giving the worker time to at least register
  // itself in the ExportJobStore before we ask for its status.
  timeoutId = setTimeout(poll, 4000);

  return () => {
    stopped = true;
    if (timeoutId) clearTimeout(timeoutId);
  };
}
